import { useEffect, useRef, useCallback, useState } from 'react';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { properties } from '../properties';

// Remove trailing '/api' from backendApi if present, then append '/ws-timer'
// In production, backendApi is '/api', so WS_URL becomes '/ws-timer' (relative to current domain)
// In local dev, backendApi is 'http://localhost:8080/api', so WS_URL becomes 'http://localhost:8080/ws-timer'
const BASE_URL = properties.backendApi.replace(/\/api\/?$/, '');
const WS_URL = `${BASE_URL}/ws-timer`;

/**
 * Custom hook that manages the full STOMP/WebSocket lifecycle for the Pomodoro timer.
 *
 * Returns:
 *  - connected: boolean — whether the WS is active
 *  - sendCommand: (command: string) => void  e.g. sendCommand('START')
 */
const useTimerWebSocket = (onStateUpdate) => {
  const clientRef = useRef(null);
  const [connected, setConnected] = useState(false);

  const sendCommand = useCallback((command) => {
    if (clientRef.current && clientRef.current.connected) {
      clientRef.current.publish({
        destination: '/app/timer/command',
        body: JSON.stringify({ command }),
      });
    } else {
      console.warn('WebSocket not connected — command dropped:', command);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const client = new Client({
      // SockJS factory — no native WebSocket so SockJS handles fallback
      webSocketFactory: () => new SockJS(WS_URL),

      connectHeaders: {
        Authorization: `Bearer ${token}`,
      },

      reconnectDelay: 5000,
      heartbeatIncoming: 20000,
      heartbeatOutgoing: 20000,

      onConnect: () => {
        setConnected(true);

        // Subscribe to personal timer state channel
        client.subscribe('/user/queue/timer', (message) => {
          try {
            const state = JSON.parse(message.body);
            if (onStateUpdate) onStateUpdate(state);
          } catch (e) {
            console.error('Failed to parse timer state:', e);
          }
        });
      },

      onDisconnect: () => {
        setConnected(false);
      },

      onStompError: (frame) => {
        console.error('STOMP error:', frame);
        setConnected(false);
      },
    });

    client.activate();
    clientRef.current = client;

    return () => {
      client.deactivate();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { connected, sendCommand };
};

export default useTimerWebSocket;
